<script lang="ts">
  // Overview > Stats — the attendance insights page.
  //
  // Two layers: a hero strip of four headline numbers, then the insight
  // feed (GET /api/hr/insights). Every card in the feed is generated from
  // real punches by internal/analytics; a builder that can't meet its
  // minimum sample size returns nothing and its card simply isn't here.
  // Nothing on this page is seeded, estimated or filled in.
  //
  // The HR-request totals that used to be the whole page are still here, as
  // a compact section at the bottom — they answer a different question
  // (what did people file) from the rest of the page (what actually
  // happened at the door).
  import { createQuery } from '@tanstack/svelte-query';
  import { hrInboxApi, hrInsightsApi, type HRInsightRangeKey } from '$lib/api/hr';
  import { fieldApi } from '$lib/api/field';
  import { session } from '$lib/state/session.svelte';
  import type { HRInsightSummary } from '$lib/types';
  import BarRow from '$lib/components/ui/BarRow.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import InsightCard from '$lib/components/ui/InsightCard.svelte';
  import PageHeader from '$lib/components/ui/PageHeader.svelte';
  import AttendanceInsightCard from './AttendanceInsightCard.svelte';
  import ReportsPanel from './ReportsPanel.svelte';

  const RANGES: { id: HRInsightRangeKey; label: string }[] = [
    { id: '7d', label: 'Week' },
    { id: '30d', label: 'Month' },
    { id: '90d', label: 'Quarter' },
    { id: '365d', label: 'Year' }
  ];

  let range = $state<HRInsightRangeKey>('30d');

  const insights = createQuery(() => ({
    queryKey: [session.isFieldScoped ? 'field-insights' : 'hr-insights', range],
    queryFn: () => session.isFieldScoped ? fieldApi.insights(range) : hrInsightsApi.get(range)
  }));

  const requests = createQuery(() => ({
    queryKey: ['hr-stats'],
    queryFn: () => hrInboxApi.stats()
  }));

  function label(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  }

  function maxOf(rows: { count: number }[]): number {
    return Math.max(1, ...rows.map((r) => r.count));
  }

  const rangeNoun = $derived(
    ({ '7d': 'week', '30d': 'month', '90d': 'quarter', '365d': 'year' } as const)[range]
  );

  // A delta is only worth showing when there is a previous period to
  // compare against — "↑100%" against a zero baseline says nothing.
  function delta(current: number, previous: number): { label: string; direction: 'up' | 'down' | 'flat' } | undefined {
    if (previous === 0) return undefined;
    const pc = ((current - previous) / previous) * 100;
    // Same 4% steady band the copy on the cards uses (analytics/copy.go).
    if (Math.abs(pc) < 4) return { label: 'steady', direction: 'flat' };
    return { label: `${Math.abs(Math.round(pc))}%`, direction: pc > 0 ? 'up' : 'down' };
  }

  function heroTiles(s: HRInsightSummary) {
    return [
      {
        label: 'Total hours',
        value: s.total_hours,
        hint: `${s.person_days} person-day${s.person_days === 1 ? '' : 's'} on the clock`,
        delta: delta(s.total_hours, s.previous_total_hours)
      },
      {
        label: 'Avg day length',
        value: `${s.avg_day_length_hours}h`,
        hint: `Across ${s.sessions} session${s.sessions === 1 ? '' : 's'}`,
        delta: delta(s.avg_day_length_hours, s.previous_avg_day_length_hours)
      },
      {
        label: 'People present',
        value: s.people_present,
        hint: `Punched in at least once this ${rangeNoun}`,
        delta: delta(s.people_present, s.previous_people_present)
      },
      {
        label: 'Auto-closed',
        value: `${s.forgot_rate_pc}%`,
        // An auto-closed session credits zero hours, so it drags the totals
        // beside it down — say that here rather than let the numbers read
        // as complete.
        hint: s.forgot_rate_pc > 0 ? 'Hours above are understated by these' : 'Every session closed properly',
        tone: (s.forgot_rate_pc > 10 ? 'bad' : s.forgot_rate_pc > 5 ? 'warn' : 'good') as
          | 'good'
          | 'warn'
          | 'bad',
        delta: delta(s.forgot_rate_pc, s.previous_forgot_rate_pc)
      }
    ];
  }
</script>

<div class="tym-page">
  <PageHeader title="Attendance insights" subtitle="What the punch data says about how the office actually runs." />

  {#if !session.isFieldScoped}
    <ReportsPanel />
  {/if}

  <FilterChipRow
    class="mb-5 mt-4"
    chips={RANGES}
    value={range}
    onSelect={(id) => (range = id as HRInsightRangeKey)}
    ariaLabel="Insight range"
  />

  {#if insights.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if insights.isError}
    <div class="tym-error-state">Could not load attendance insights.</div>
  {:else if insights.data}
    {@const feed = insights.data}

    <div class="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {#each heroTiles(feed.summary) as tile (tile.label)}
        <InsightCard
          label={tile.label}
          value={tile.value}
          hint={tile.hint}
          tone={tile.tone}
          delta={tile.delta}
        />
      {/each}
    </div>

    {#if feed.insights.length === 0}
      <div class="tym-card">
        <EmptyState
          title="No punches in this window"
          body="Insights appear once there are enough real punches to say something true about them."
        />
      </div>
    {:else}
      <!-- Featured cards span two columns on a wide screen; everything is
           one column on a phone. -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {#each feed.insights as card (card.id)}
          <div class={card.render.layout_hint === 'featured' ? 'lg:col-span-2' : ''}>
            <AttendanceInsightCard {card} />
          </div>
        {/each}
      </div>
    {/if}
  {/if}

  <!-- HR request totals: what people filed, as distinct from what the
       punches recorded. Kept from the previous version of this page. -->
  {#if requests.data}
    {@const s = requests.data}
    <!-- GORM's Scan leaves a nil ([]) slice — and thus a JSON `null` — for
         zero-row aggregates (e.g. a brand-new org with no HR requests yet
         at all), so every array here is nullable despite the response type
         saying otherwise; default each to [] before touching .length/#each. -->
    {@const byType = s.by_type ?? []}
    {@const leaveByReason = s.leave_by_reason_category ?? []}
    {@const perUser = s.per_user_totals ?? []}
    {@const byStatus = s.by_status ?? []}

    <section class="mt-8">
      <h2 class="tym-section-title text-base">HR requests</h2>
      <p class="tym-section-sub mb-3.5">Request totals for {s.year} — filed, not punched.</p>

      {#if byStatus.length > 0}
        <div class="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {#each byStatus as row (row.key)}
            <div class="tym-card p-4">
              <p class="tym-section-sub mb-1">{label(row.key)}</p>
              <p class="tym-stat-value text-2xl">{row.count}</p>
            </div>
          {/each}
        </div>
      {/if}

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div class="tym-card p-5">
          <p class="tym-section-title mb-4 text-base">By type</p>
          {#if byType.length > 0}
            {@const max = maxOf(byType)}
            <div class="space-y-3">
              {#each byType as row (row.key)}
                <BarRow label={label(row.key)} value={row.count} {max} />
              {/each}
            </div>
          {:else}
            <p class="tym-empty-state !p-0 text-left">No requests logged yet this year.</p>
          {/if}
        </div>

        <div class="tym-card p-5">
          <p class="tym-section-title mb-4 text-base">Leave by reason</p>
          {#if leaveByReason.length > 0}
            {@const max = maxOf(leaveByReason)}
            <div class="space-y-3">
              {#each leaveByReason as row (row.key)}
                <BarRow label={label(row.key)} value={row.count} {max} />
              {/each}
            </div>
          {:else}
            <p class="tym-empty-state !p-0 text-left">No leave requests logged yet this year.</p>
          {/if}
        </div>

        <div class="tym-card p-5">
          <p class="tym-section-title mb-4 text-base">By user (this year)</p>
          {#if perUser.length > 0}
            {@const max = maxOf(perUser)}
            <div class="space-y-3">
              {#each perUser as row (row.user_id)}
                <BarRow label={row.user_name || `User #${row.user_id}`} value={row.count} {max} />
              {/each}
            </div>
          {:else}
            <p class="tym-empty-state !p-0 text-left">No one has filed a request yet this year.</p>
          {/if}
        </div>
      </div>
    </section>
  {/if}
</div>
